import { Component, Host, h, Prop, Watch, JSX, Element, Fragment, State, Listen } from '@stencil/core';
import { fetchIcons, renderInfographic, type valueChainPoint } from './infographic';
import { responsive } from '../../global/utility/responsive/responsive';
import { replaceCheckmarkIcons } from '../../global/utility/utils';

interface UiCmsValueChainBody {
  valueChainPoints: valueChainPoint[];
}

@Component({
  tag: 'ui-cms-value-chain',
  styleUrl: 'ui-cms-value-chain.scss',
  shadow: true,
})
export class UiCmsValueChain {
  @Element() el: HTMLUiCmsValueChainElement;

  @Prop({ mutable: true }) body: UiCmsValueChainBody = {
    valueChainPoints: [],
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Prop({ mutable: true }) i18n = {
    startAnimation: 'Animation starten',
    stopAnimation: 'Animation stoppen',
  };

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? { ...this.body, ...JSON.parse(newValue) } : this.body;
  }

  @Watch('utilityI18n') utilityI18nChangeHandler(newValue: string) {
    if (newValue) {
      this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
    }
  }

  @Listen('sd-hide') onDialogHide() {
    const itemToFocus = this.el.shadowRoot.getElementById(
      `dialog-trigger-${this.valueChainPointItem.id}`
    ) as HTMLElement;

    itemToFocus.focus();
  }

  /** disables the animation, currently only used for screenshot tests  */
  @Prop()
  noAnimation = false;

  /** componentWidth can be 0 or 768.
   * 0 means that the component is smaller than or equal to 768px.
   * 768 means that the component is wider than 768px.
   */
  @Prop({ reflect: true }) componentWidth: number = window.innerWidth <= 768 ? 0 : 768;

  @State() animationTriggered = false;

  @State() valueChainPointItem: valueChainPoint;

  private infographicRef: HTMLDivElement;

  private resizeObserver: ResizeObserver;

  private intersectionObserver: IntersectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.animationTriggered = true;
        this.intersectionObserver.unobserve(this.infographicRef);
      }
    });
  });

  /** stringified svg icons */
  private icons: string[];

  private items: valueChainPoint[] = [];

  private get layout(): 'small' | 'large' {
    return this.componentWidth === 0 ? 'small' : 'large';
  }

  private get oddItems(): valueChainPoint[] {
    return this.items.filter((item) => item.id % 2 !== 1);
  }

  private get evenItems(): valueChainPoint[] {
    return this.items.filter((item) => item.id % 2 === 1);
  }

  private getItemStep(itemId: number): number {
    return this.items.findIndex((val) => val.id === itemId) + 1;
  }

  private setItems(): void {
    this.items = this.body.valueChainPoints.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
  }

  private async setIcons(): Promise<void> {
    this.icons = await fetchIcons(this.items);
  }

  private setLayout(): void {
    this.el.style.setProperty('--ui-cms-value-chain-items', this.items.length.toString());
  }

  private showDialog(item: valueChainPoint): void {
    this.valueChainPointItem = item;
    (this.el.shadowRoot.getElementById('value-chain-dialog') as HTMLUiDialogElement).show();
  }

  private toggleAnimation() {
    this.animationTriggered = !this.animationTriggered;
  }

  private get controlsContent() {
    return this.animationTriggered
      ? { icon: 'union-investment/system/pause', text: this.i18n.stopAnimation }
      : { icon: 'union-investment/system/start', text: this.i18n.startAnimation };
  }

  async componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
    this.setItems();
    this.setLayout();
    await this.setIcons();
  }

  componentDidLoad() {
    this.resizeObserver = responsive(this.el);
    if (!this.noAnimation) this.intersectionObserver.observe(this.infographicRef);
    replaceCheckmarkIcons(this.el.shadowRoot);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
  }

  private renderSmallLayout(): JSX.Element {
    return (
      <Fragment>
        <div
          class="valueChain__infographic"
          ref={(el) => {
            this.infographicRef = el;
          }}
        >
          {renderInfographic(this.items, this.icons, 'small')}
        </div>
        <ol class="valueChain__itemWrapper">{this.items.map((item) => this.renderItem(item))}</ol>
      </Fragment>
    );
  }

  private renderLargeLayout(): JSX.Element {
    return (
      <Fragment>
        <ol class="valueChain__itemWrapper">{this.evenItems.map((item) => this.renderItem(item))}</ol>
        <div
          class="valueChain__infographic"
          ref={(el) => {
            this.infographicRef = el;
          }}
        >
          {renderInfographic(this.items, this.icons, 'large')}
        </div>
        <ol class="valueChain__itemWrapper">{this.oddItems.map((item) => this.renderItem(item))}</ol>
      </Fragment>
    );
  }

  private renderItem(item: valueChainPoint): JSX.Element {
    return this.componentWidth === 0 ? (
      <li class={`valueChain__item valueChain__item${item.id}`}>
        <button
          id={`dialog-trigger-${item.id}`}
          aria-haspopup="dialog"
          class="sd-interactive sd-interactive--reset"
          onClick={() => this.showDialog(item)}
        >
          {item.headline}
        </button>
      </li>
    ) : (
      <li class={`valueChain__item valueChain__item${item.id}`} style={{ gridColumnStart: item.id.toString() }}>
        <span class="sr-only">{`Schritt ${this.getItemStep(item.id)}`}</span>
        <p class="sd-headline sd-headline--size-base">{item.headline}</p>
        <div class="sd-prose" innerHTML={item.text}></div>
      </li>
    );
  }

  render() {
    return (
      <Host>
        <sd-button variant="tertiary" size="sm" onClick={() => this.toggleAnimation()}>
          <sd-icon slot="icon-left" library={window.ui.cmsState.iconLibrary} name={this.controlsContent.icon}></sd-icon>
          {this.controlsContent.text}
        </sd-button>
        <div
          class={{
            valueChain: true,
            'valueChain--animation': !this.noAnimation && this.animationTriggered,
            'valueChain--animationTriggered': this.animationTriggered,
          }}
        >
          {this.layout === 'small' ? this.renderSmallLayout() : this.renderLargeLayout()}
        </div>
        <sd-dialog id="value-chain-dialog" open="">
          <span slot="headline" class="sd-headline sd-headline--size-3xl">
            {this.valueChainPointItem?.headline}
          </span>
          <div class="sd-prose" innerHTML={this.valueChainPointItem?.text}></div>
        </sd-dialog>
      </Host>
    );
  }
}
